import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { loadCachedGltfClone } from 'src/utils/capsyModelCache';

interface CapsyCharacterProps {
  modelScale?: number;
  staticPose?: boolean;
}

const CAPSY_AI_MODEL_PATH = "/capsy_ai_drug_developer.glb?v=capsy-ai-front-inner-rotation-20260713";
const CAPSY_AI_FRONT_ROTATION_Y = -Math.PI / 2;

export default function CapsyCharacter({ modelScale = 1, staticPose = false }: CapsyCharacterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setClearColor(0xffffff, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = 1.18;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-3, 3, 1.5, -1.5, 0.1, 3000);
    camera.position.z = 1200;
    camera.lookAt(0, 0, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 2));
    scene.add(new THREE.HemisphereLight(0xffffff, 0xdde5ef, 1.1));
    const dir = new THREE.DirectionalLight(0xffffff, 2.1);
    dir.position.set(0.2, 1.4, 1.8);
    scene.add(dir);

    const mouse = { x: 0, y: 0 };
    const smoothed = { x: 0, y: 0 };
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouse.x = Math.max(-0.5, Math.min(0.5, (e.clientX - centerX) / rect.width));
      mouse.y = Math.max(-0.5, Math.min(0.5, -(e.clientY - centerY) / rect.height));
    };
    if (!staticPose) {
      window.addEventListener('mousemove', onMove);
    }

    const updateSize = (width: number, height: number) => {
      renderer.setSize(width, height, false);
      const aspect = width / height;
      camera.left = -3;
      camera.right = 3;
      camera.top = 3 / aspect;
      camera.bottom = -3 / aspect;
      camera.updateProjectionMatrix();
    };

    let resizeFrame = 0;
    let lastWidth = 0;
    let lastHeight = 0;

    // ResizeObserver로 canvas 실제 크기 변화 감지
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width, height } = entry.contentRect;
      const nextWidth = Math.round(width);
      const nextHeight = Math.round(height);

      if (nextWidth <= 0 || nextHeight <= 0) return;
      if (nextWidth === lastWidth && nextHeight === lastHeight) return;

      lastWidth = nextWidth;
      lastHeight = nextHeight;
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => updateSize(nextWidth, nextHeight));
    });
    ro.observe(canvas);
    updateSize(canvas.clientWidth || 1, canvas.clientHeight || 1);

    let capsyGroup: THREE.Group | null = null;
    let mixer: THREE.AnimationMixer | null = null;
    let previousTime = performance.now();
    let frameId = 0;
    let disposed = false;

    loadCachedGltfClone(`${process.env.PUBLIC_URL}${CAPSY_AI_MODEL_PATH}`).then((gltf) => {
      if (disposed) return;

      const box = new THREE.Box3().setFromObject(gltf.scene);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = (2.2 * modelScale) / maxDim;
      gltf.scene.scale.setScalar(scale);
      gltf.scene.position.sub(center.multiplyScalar(scale));

      const baseGroup = new THREE.Group();
      baseGroup.rotation.y = CAPSY_AI_FRONT_ROTATION_Y;
      baseGroup.add(gltf.scene);

      if (gltf.animations.length > 0 && !prefersReducedMotion && !staticPose) {
        mixer = new THREE.AnimationMixer(gltf.scene);
        gltf.animations.forEach((clip) => {
          mixer?.clipAction(clip).play();
        });
      }

      capsyGroup = new THREE.Group();
      capsyGroup.rotation.order = 'YXZ';
      capsyGroup.add(baseGroup);
      scene.add(capsyGroup);

      const animate = () => {
        frameId = requestAnimationFrame(animate);
        const now = performance.now();
        const delta = Math.min((now - previousTime) / 1000, 0.05);
        previousTime = now;
        if (mixer && !prefersReducedMotion && !staticPose) {
          mixer.update(delta);
        }
        const ease = Math.min(12 * delta, 1);
        smoothed.x += (mouse.x - smoothed.x) * ease;
        smoothed.y += (mouse.y - smoothed.y) * ease;
        if (capsyGroup && !prefersReducedMotion && !staticPose) {
          capsyGroup.rotation.y = smoothed.x * 1.4;
          capsyGroup.rotation.x = -smoothed.y * 0.9;
        }
        renderer.render(scene, camera);
      };
      if (staticPose) {
        renderer.render(scene, camera);
      } else {
        animate();
      }
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      cancelAnimationFrame(resizeFrame);
      if (!staticPose) {
        window.removeEventListener('mousemove', onMove);
      }
      ro.disconnect();
      if (mixer) {
        mixer.stopAllAction();
      }
      renderer.dispose();
    };
  }, [modelScale, staticPose]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
}
